import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Info } from '@mui/icons-material'
import url from 'libraries/front-end-core/core/url'
import { ALL_AOLS, AOL_DISPLAY_NAMES } from 'constants/aol/aol'
import { SMART_SEARCH } from 'constants/controllers'
import { ALL_FLAVOURS, FLAVOUR_TITLES } from 'constants/flavours'
import selectUserAccessRoles from 'redux/selectors/selectAccessRoles'
import TealToolTip from 'components/TealToolTip'
import FlavourSelector from 'components/shared/FlavourSelector'
import FlavourIcon from 'components/shared/FlavourIcon'
import AOLDropdown from 'components/shared/AOLDropdown'

const hasAccess = (aol, accessRoles) => accessRoles.some(role => role.startsWith(`${aol}_qa`))

const SmartSearchHeader = ({ query = {} }) => {
  const [caseSearchInfoOpen, setCaseSearchInfoOpen] = useState(false)
  const flavour = useSelector(state => state.layout.flavour)
  const caseSearch = query?.boostCases === 'true'
  // Uses the lowercase aol key (not AOL_DISPLAY_NAMES) for the URL value,
  // consistent with the rest of the app's routing and more URL-friendly.
  // selectedAols is still normalized case-insensitively so an old bookmarked
  // link built with the previous (capitalized) convention still shows the
  // right filter as selected instead of silently appearing unfiltered.
  const selectedAols = query.aol
    ? [].concat(query.aol).map(aol => aol.toLowerCase())
    : []
  const accessRoles = useSelector(selectUserAccessRoles)
  const aolOptions = ALL_AOLS.map(aol => {
    const displayName = AOL_DISPLAY_NAMES[aol]
    const isSelected = selectedAols.includes(aol)

    return {
      label: displayName,
      href: url(
        SMART_SEARCH,
        { flavour },
        {
          ...query,
          aol: isSelected
            ? selectedAols.filter(selectedAol => selectedAol !== aol)
            : [...selectedAols, aol]
        }
      ),
      selected: isSelected,
      disabled: !hasAccess(aol, accessRoles)
    }
  })

  const flavourOptions = ALL_FLAVOURS.map(flavour => ({
    label: (
      <div className='flex items-center gap5'>
        <FlavourIcon flavour={flavour} bw />
        {FLAVOUR_TITLES[flavour]}
      </div>
    ),
    href: url(SMART_SEARCH, { flavour }, { q: query.q })
  }))

  return (
    <div className='bg-page-background w-100'>
      <div className='layout-width-constraint pt6 pb3 flex justify-between items-start flex-column-m gap5-m'>
        <span className='fht fw6'>Q&A Search results</span>
        <span className='flex gap7 flexy-m gap5-m items-end flex-wrap-m'>
          <div>
            <label className='flex items-center f6'>
              Case search
              <span
                role='button'
                tabIndex={0}
                aria-label='Case search info'
                data-testid='case-search-info'
                className='ml2 f5 w6 hs6 flex items-center justify-center pointer'
                onClick={(e) => {
                  e.preventDefault()
                  // Explicitly open rather than toggle - browsers dispatch a
                  // compatibility mouseenter right before click on touch taps
                  // (and Cypress's .click() does the same to simulate a real
                  // cursor), which would open it via onMouseEnter just before
                  // a toggle here closed it straight back again
                  setCaseSearchInfoOpen(true)
                }}
                onMouseEnter={() => setCaseSearchInfoOpen(true)}
                onMouseLeave={() => setCaseSearchInfoOpen(false)}
                onBlur={() => setCaseSearchInfoOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setCaseSearchInfoOpen(false)
                }}
              >
                <TealToolTip
                  title='If you are searching for a specific case reference, checking this will boost case reference results.'
                  placement='bottom'
                  arrow={false}
                  bgColor='dark'
                  open={caseSearchInfoOpen}
                >
                  <Info className='fill-medium-bluish-grey hover-fill-light-slate-grey' />
                </TealToolTip>
              </span>
            </label>
            <div className='pt4 flex items-center'>
              <a
                href={url(SMART_SEARCH, { flavour }, { ...query, boostCases: !caseSearch })}
                className='pointer black no-underline flex items-center pointer'
              >
                <input
                  type='checkbox'
                  checked={caseSearch}
                  data-event-category='Smart Search'
                  data-event-action={caseSearch ? 'Disable Case Search' : 'Enable Case Search'}
                  className='pointer'
                />
                <label className='f6 mh4 pointer'>
                  Search for a specific case
                </label>
              </a>
            </div>
          </div>
          <div className='flex items-center gap7 flex-wrap-m gap5-m'>
            <FlavourSelector
              options={flavourOptions}
            />
            <AOLDropdown
              multi
              align='left'
              options={aolOptions}
              testId='aol-dropdown'
            />
          </div>
        </span>
      </div>
    </div>
  )
}

export default SmartSearchHeader
